import React from "react";
import { AiOutlineLoading } from "react-icons/ai";
import { MyButton } from "./ButtonUtils";

/**
 * TIP: If you have a Tailwind `brand` color, you can swap `bg-gray-900 hover:bg-brand/90`
 * for `bg-brand hover:bg-brand/90` to align with your palette.
 */

export const ActionButton = ({ props }: { props: any }) => {
  const base =
    "relative group inline-flex items-center justify-center " +
    "w-full md:w-3/12 h-14 min-w-fit px-6 my-10 rounded-xl " +
    "text-white font-semibold tracking-wide capitalize " +
    "transition duration-200 ease-out " +
    "bg-brand hover:bg-brand/90 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 " +
    "shadow-sm hover:shadow-md active:shadow disabled:opacity-60 disabled:pointer-events-none " +
    "overflow-hidden";
  const glow =
    "before:absolute before:inset-0 before:bg-white/0 before:transition before:duration-200 " +
    "group-hover:before:bg-white/[0.06]";
  const sheen =
    "after:absolute after:inset-y-0 after:-left-1/3 after:w-1/3 after:bg-white/20 " +
    "after:skew-x-12 after:opacity-0 group-hover:after:opacity-100 " +
    "group-hover:after:translate-x-[260%] after:transition-transform after:duration-700";

  const classes = `${base} ${glow} ${sheen} ${props?.className || ""}`;

  return props.loading ? (
    <button
      type="button"
      className={classes}
      aria-busy="true"
      aria-live="polite"
      disabled
      data-loading="true"
    >
      <AiOutlineLoading className="w-6 h-6 animate-spin motion-reduce:animate-none" />
      <span className="sr-only">Loading</span>
    </button>
  ) : (
    <button
      type="submit"
      className={classes}
      aria-busy="false"
      data-loading="false"
    >
      <span>{props.title}</span>
    </button>
  );
};

type MyActionButtonProps = {
  type?: "button" | "submit" | "reset";
  loading?: any;
  title?: any;
  onClick?: any;
  className?: string;
};

export const MyActionButton = ({
  type = "submit",
  loading,
  title,
  onClick,
  className,
}: MyActionButtonProps) => {
  const base =
    "relative group inline-flex items-center justify-center " +
    "min-w-fit w-fit px-8 py-3 my-8 rounded-xl text-sm font-semibold text-white " +
    "bg-brand hover:bg-brand/90 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 " +
    "shadow-sm hover:shadow-md active:shadow disabled:opacity-60 disabled:pointer-events-none " +
    "overflow-hidden";
  const glow =
    "before:absolute before:inset-0 before:bg-white/0 before:transition before:duration-200 " +
    "group-hover:before:bg-white/[0.06]";
  const sheen =
    "after:absolute after:inset-y-0 after:-left-1/3 after:w-1/3 after:bg-white/20 " +
    "after:skew-x-12 after:opacity-0 group-hover:after:opacity-100 " +
    "group-hover:after:translate-x-[260%] after:transition-transform after:duration-700";

  return (
    <MyButton
      type={type || "submit"}
      onClick={onClick}
      disabled={!!loading}
      className={`${base} ${glow} ${sheen} ${className || ""}`}
    >
      {loading ? (
        <span className="flex items-center gap-3">
          <AiOutlineLoading className="h-5 w-5 animate-spin motion-reduce:animate-none" />
          <span className="sr-only">Loading</span>
        </span>
      ) : (
        <span className="whitespace-nowrap">{title}</span>
      )}
    </MyButton>
  );
};
